import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { chatAPI, subscriptionAPI } from '../utils/api';
import { generateAudio, getTtsDebugInfo, playAudio, primeAudioPlayback } from '../utils/audio';
import './ChatPage.scss';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  isHidden?: boolean;
}

const ChatPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const ttsDebug = getTtsDebugInfo();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [sessionId, setSessionId] = useState<number | null>(location.state?.sessionId || null);
  const [sessionLoading, setSessionLoading] = useState(!location.state?.sessionId);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [messagesLeft, setMessagesLeft] = useState<number>(0);
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    checkSubscription();
    if (sessionId) {
      setSessionLoading(false);
      loadMessages();
    } else if (sessionLoading) {
      createSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkSubscription = async () => {
    try {
      const response = await subscriptionAPI.getSubscription();
      if (response.data.subscription && response.data.subscription.is_active) {
        setHasSubscription(true);
        setMessagesLeft(response.data.subscription.messages_limit - response.data.subscription.messages_used);
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const createSession = async (): Promise<number | null> => {
    setSessionError(null);
    try {
      const response = await chatAPI.createSession();
      const id = response.data.session?.id;
      if (id != null) {
        const numId = Number(id);
        setSessionId(numId);
        setSessionLoading(false);
        return numId;
      }
      setSessionError(t('chat.errors.sessionFailed'));
      return null;
    } catch (error) {
      console.error('Error creating session:', error);
      setSessionError(t('chat.errors.sessionFailed'));
      return null;
    } finally {
      setSessionLoading(false);
    }
  };

  const loadMessages = async () => {
    if (!sessionId) return;
    try {
      const response = await chatAPI.getMessages(sessionId);
      const loadedMessages = response.data.messages.map((msg: any) => ({
        id: msg.id.toString(),
        text: msg.message_text,
        sender: msg.sender,
        timestamp: new Date(msg.created_at),
        isHidden: msg.sender === 'ai' && !hasSubscription && response.data.messages.indexOf(msg) === response.data.messages.length - 1,
      }));
      setMessages(loadedMessages);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const simulateTyping = async (delay: number) => {
    setIsTyping(true);
    await new Promise(resolve => setTimeout(resolve, delay));
    setIsTyping(false);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;
    let sid = sessionId;
    if (!sid) {
      sid = await createSession();
      if (!sid) return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    const messageText = inputValue;
    setInputValue('');

    if (!hasSubscription) {
      setIsConnecting(true);
      await simulateTyping(2000 + Math.random() * 3000);
      setIsConnecting(false);

      try {
        const response = await chatAPI.sendMessage(sid, messageText);
        const aiMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: response.data.message,
          sender: 'ai',
          timestamp: new Date(),
          isHidden: true,
        };
        setMessages(prev => [...prev, aiMessage]);
      } catch (error) {
        console.error('Error sending message:', error);
      }
    } else {
      setIsTyping(true);
      try {
        const response = await chatAPI.sendMessage(sid, messageText);
        await simulateTyping(1000 + Math.random() * 2000);
        const aiMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: response.data.message,
          sender: 'ai',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, aiMessage]);
        setMessagesLeft(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Error sending message:', error);
      } finally {
        setIsTyping(false);
      }
    }
  };

  const handleRevealMessage = () => {
    navigate('/subscription', { state: { returnTo: 'chat', sessionId } });
  };

  return (
    <div className="chat-page">
      <div className="chat-page__container">
        <header className="chat-page__header">
          <h1 className="chat-page__title">{t('nav.aiChat')}</h1>
        </header>
        <div className="chat-page__messages">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`message message--${message.sender} ${message.isHidden ? 'message--hidden' : ''}`}
            >
              <div className="message__content">
                {message.isHidden ? (
                  <div className="message__hidden">
                    <div className="message__blur">{message.text}</div>
                    <button className="btn-primary" onClick={handleRevealMessage}>
                      {t('chat.payment.reveal')}
                    </button>
                  </div>
                ) : (
                  <p>{message.text}</p>
                )}
                {message.sender === 'ai' && !message.isHidden && (
                  <button 
                    className="message__audio-btn"
                    onClick={async () => {
                      if (playingAudio === message.id) return;
                      // Разблокируем звук СИНХРОННО в жесте клика (иначе Safari блокирует play()).
                      primeAudioPlayback();
                      setPlayingAudio(message.id);
                      try {
                        const audioUrl = await generateAudio(message.text);
                        if (audioUrl) {
                          await playAudio(audioUrl);
                        }
                      } catch (error) {
                        console.error('Error playing audio:', error);
                      } finally {
                        setPlayingAudio(null);
                      }
                    }}
                    disabled={playingAudio === message.id}
                    title={`TTS: ${ttsDebug.provider}${ttsDebug.voiceId ? ` | voiceId: ${ttsDebug.voiceId}` : ''}`}
                  >
                    {playingAudio === message.id ? '⏸' : '▶'} {t('chat.audio.play')}
                  </button>
                )}
              </div>
              <span className="message__time">
                {message.timestamp.toLocaleTimeString()}
              </span>
            </div>
          ))}
          {isConnecting && (
            <div className="message message--ai">
              <div className="message__content">
                <p>{t('chat.status.connecting')}</p>
              </div>
            </div>
          )}
          {isTyping && (
            <div className="message message--ai">
              <div className="message__content">
                <p className="typing-indicator">{t('chat.status.typing')}</p>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {sessionError && (
          <div className="chat-page__error">
            {sessionError}
            <button type="button" className="btn-secondary btn-secondary--small" onClick={() => { setSessionError(null); setSessionLoading(true); createSession(); }}>
              {t('chat.input.retry')}
            </button>
          </div>
        )}
        {sessionLoading && !sessionError && (
          <div className="chat-page__status">{t('chat.status.sessionCreating')}</div>
        )}
        <div className="chat-page__input">
          <input
            type="text"
            className="chat-input"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !sessionLoading && !isTyping && handleSendMessage()}
            placeholder={t('chat.input.placeholder')}
            disabled={sessionLoading && !sessionId}
          />
          <button
            type="button"
            className="btn-primary"
            onClick={handleSendMessage}
            disabled={(sessionLoading && !sessionId) || isTyping || !inputValue.trim()}
          >
            {sessionLoading && !sessionId ? t('chat.status.sessionCreating') : isTyping ? t('chat.status.typing') : t('chat.input.send')}
          </button>
        </div>

        {hasSubscription && (
          <div className="chat-page__counter">
            {t('chat.messagesLeft', { count: messagesLeft })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatPage;
